import { useMemo, useState } from 'react';
import {
  Title3, Body1, Button, Input, Field, RadioGroup, Radio, Textarea,
  Badge, tokens, Card,
} from '@fluentui/react-components';
import { adminSetTenantPlan, adminSimulatePack, type SimulatedPlan, type PackCounter } from '../api/admin.js';
import { useAuthStore } from '../stores/authStore.js';
import { AppTopBar } from '../components/AppTopBar.js';
import { Breadcrumbs } from '../components/Breadcrumbs.js';

/**
 * Página interna (Ecosoft staff) de simulación del webhook de ADLIC.
 *
 * Doble propósito:
 *  1) DOCUMENTAR: muestra el request HTTP exacto que ADLIC (Odoo) debe enviar
 *     a m0 para cambiar el plan o registrar la compra de un paquete. Los
 *     implementadores de ADLIC copian de acá.
 *  2) SIMULAR: ejecuta el efecto vía endpoints admin (sesión staff), sin
 *     exponer el secret de ADLIC en el browser.
 */
type Op = 'plan' | 'pack';

export function AdlicSimPage() {
  // La página está gateada a staff de Ecosoft, así que el tenant de la sesión
  // ES el de Ecosoft. Lo usamos como default para probar rápido.
  const ecosoftTenant = useAuthStore((s) => s.tenant);
  const currentUser = useAuthStore((s) => s.user);
  const [tenantId, setTenantId] = useState(ecosoftTenant?.id ?? '');
  // Modelo de planes por usuario: ADLIC informa por (userId, tenantId). Default
  // = el propio staff; cambiar para simular sobre otro usuario del tenant.
  const [userId, setUserId] = useState(currentUser?.id ?? '');
  const [op, setOp] = useState<Op>('plan');
  const [plan, setPlan] = useState<SimulatedPlan>('pro');
  const [counterType, setCounterType] = useState<PackCounter>('concepts_quoted');
  const [amount, setAmount] = useState('500');
  const [externalRef, setExternalRef] = useState('');
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const base = window.location.origin;
  const idForUrl = tenantId.trim() || '{tenantId}';
  const userForUrl = userId.trim() || '{userId}';

  const { url, body } = useMemo(() => {
    const scope = `${base}/api/v1/adlic/tenants/${idForUrl}/users/${userForUrl}`;
    if (op === 'plan') {
      return { url: `${scope}/plan`, body: { plan } as Record<string, unknown> };
    }
    const b: Record<string, unknown> = { counterType, amount: Number(amount) || 0 };
    if (externalRef.trim()) b.externalRef = externalRef.trim();
    return { url: `${scope}/packs`, body: b };
  }, [op, plan, counterType, amount, externalRef, base, idForUrl, userForUrl]);

  const curl = `curl -X POST "${url}" \\\n  -H "Content-Type: application/json" \\\n  -H "x-adlic-secret: <ADLIC_WEBHOOK_SECRET>" \\\n  -d '${JSON.stringify(body)}'`;

  async function simulate() {
    setBusy(true); setError(null); setResult(null);
    try {
      if (!tenantId.trim()) throw new Error('Ingresa el tenantId (UUID) del tenant a modificar.');
      if (!userId.trim()) throw new Error('Ingresa el userId (UUID) del usuario a modificar.');
      const r = op === 'plan'
        ? await adminSetTenantPlan(tenantId.trim(), plan, userId.trim())
        : await adminSimulatePack(tenantId.trim(), { counterType, amount: Number(amount), userId: userId.trim() });
      setResult(JSON.stringify(r, null, 2));
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  function copyCurl() {
    void navigator.clipboard.writeText(curl);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  const mono = { fontFamily: 'monospace', fontSize: tokens.fontSizeBase200, whiteSpace: 'pre-wrap' as const };

  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)' }}>
      <AppTopBar />
      <Breadcrumbs items={[{ label: 'Inicio', to: '/projects' }, { label: 'Simulación ADLIC (staff)' }]} />
      <div style={{ maxWidth: 880, margin: '0 auto', padding: '24px 16px', display: 'flex', flexDirection: 'column', gap: 16 }}>
      <div>
        <Title3>Simulación ADLIC → m0</Title3>
        <Body1 as="p" style={{ color: tokens.colorNeutralForeground3, marginTop: 4 }}>
          Herramienta interna (Ecosoft staff) para simular el webhook que ADLIC (Odoo) envía a m0
          cuando un tenant cambia de plan o compra un paquete. m0 no factura ni emite CFDI — solo
          refleja el cambio para ajustar la cuota.
        </Body1>
      </div>

      <Card style={{ padding: 16, borderLeft: `4px solid ${tokens.colorPaletteYellowBorderActive}` }}>
        <Body1 style={{ fontWeight: tokens.fontWeightSemibold, marginBottom: 6 }}>
          ⚠️ Renovación de licencia OPUS 27 — ADLIC debe avisar a m0
        </Body1>
        <Body1 as="p" style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2, marginBottom: 6 }}>
          Cuando vence la licencia, m0 entra en modo solo-lectura y archiva los archivos del usuario.
          A los 30 días sin renovar, los archivos se borran. Si el usuario renueva dentro del mes
          de gracia, hay que reactivarlos.
        </Body1>
        <Body1 as="p" style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2, marginBottom: 6 }}>
          <strong>m0 NO consulta a Anywhere por renovaciones.</strong> Se entera porque ADLIC dispara
          el mismo webhook de plan que ves abajo — incluso cuando el usuario renueva con el mismo
          plan, ADLIC debe enviarlo (es idempotente: <code>UPDATE</code> con el mismo valor es no-op,
          pero le dice a m0 "esto sigue activo" y reactiva los archivos archivados).
        </Body1>
        <Body1 as="p" style={{ fontSize: tokens.fontSizeBase200, color: tokens.colorNeutralForeground2 }}>
          <strong>El plan del usuario NO se baja a start al vencer la licencia</strong> — queda como
          estaba. Sólo cambia si ADLIC envía un cambio explícito. Esto preserva la información del
          plan vigente y simplifica la reactivación al renovar.
        </Body1>
      </Card>

      <Card style={{ display: 'flex', flexDirection: 'column', gap: 12, padding: 16 }}>
        <Field
          label="Tenant ID (UUID)"
          hint={
            ecosoftTenant && tenantId === ecosoftTenant.id
              ? `Por defecto: tu tenant (${ecosoftTenant.name}). Cambialo para simular sobre otro tenant.`
              : 'UUID del tenant a modificar.'
          }
        >
          <Input value={tenantId} onChange={(_, d) => setTenantId(d.value)} placeholder="fa29648c-3f6b-4e37-b37d-c91e5b0d07f0" />
        </Field>

        <Field
          label="User ID (UUID)"
          hint={
            currentUser && userId === currentUser.id
              ? `Por defecto: tu usuario (${currentUser.name}). El plan/paquete es por usuario; cámbialo para simular sobre otro.`
              : 'UUID del usuario a modificar dentro del tenant.'
          }
        >
          <Input value={userId} onChange={(_, d) => setUserId(d.value)} placeholder="11111111-1111-1111-1111-111111111111" />
        </Field>

        <Field label="Operación">
          <RadioGroup value={op} onChange={(_, d) => setOp(d.value as Op)} layout="horizontal">
            <Radio value="plan" label="Cambio de plan" />
            <Radio value="pack" label="Compra de paquete" />
          </RadioGroup>
        </Field>

        {op === 'plan' ? (
          <Field label="Plan">
            <RadioGroup value={plan} onChange={(_, d) => setPlan(d.value as SimulatedPlan)} layout="horizontal">
              <Radio value="start" label="start" />
              <Radio value="pro" label="pro" />
              <Radio value="teams" label="teams" />
              <Radio value="vitruvio" label="vitruvio" />
            </RadioGroup>
          </Field>
        ) : (
          <>
            <Field label="Tipo de cuota">
              <RadioGroup value={counterType} onChange={(_, d) => setCounterType(d.value as PackCounter)} layout="horizontal">
                <Radio value="concepts_quoted" label="conceptos a cotizar" />
                <Radio value="chats_sent" label="chats" />
              </RadioGroup>
            </Field>
            <Field label="Cantidad">
              <Input type="number" value={amount} onChange={(_, d) => setAmount(d.value)} />
            </Field>
            <Field label="externalRef (id de la compra en Odoo — opcional, evita duplicados en reintentos)">
              <Input value={externalRef} onChange={(_, d) => setExternalRef(d.value)} placeholder="odoo-so-12345" />
            </Field>
          </>
        )}
      </Card>

      <Card style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: 16 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Body1 style={{ fontWeight: tokens.fontWeightSemibold }}>Petición que debe enviar ADLIC</Body1>
          <Button size="small" onClick={copyCurl}>{copied ? '✓ Copiado' : 'Copiar'}</Button>
        </div>
        <div style={{ ...mono, background: tokens.colorNeutralBackground3, padding: 12, borderRadius: 6 }}>{curl}</div>
        <Body1 as="p" style={{ color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 }}>
          El header <code>x-adlic-secret</code> lo provee Ecosoft por entorno (no se muestra aquí por seguridad).
          Doc completa: <code>docs/integracion-m1/adlic-planes-paquetes.md</code>.
        </Body1>
      </Card>

      <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
        <Button appearance="primary" disabled={busy} onClick={simulate}>
          {busy ? 'Ejecutando…' : 'Simular ahora (staff)'}
        </Button>
        <Body1 style={{ color: tokens.colorNeutralForeground3, fontSize: tokens.fontSizeBase200 }}>
          Ejecuta el efecto real vía sesión staff (sin el secret de ADLIC).
        </Body1>
      </div>

      {error && (
        <Card style={{ padding: 12, borderLeft: `4px solid ${tokens.colorPaletteRedBorderActive}` }}>
          <Badge color="danger" appearance="tint">Error</Badge>
          <div style={{ ...mono, marginTop: 6 }}>{error}</div>
        </Card>
      )}
      {result && (
        <Card style={{ padding: 12, borderLeft: `4px solid ${tokens.colorPaletteGreenBorderActive}` }}>
          <Badge color="success" appearance="tint">Respuesta</Badge>
          <Textarea value={result} readOnly resize="vertical" style={{ marginTop: 6, width: '100%', ...mono }} />
        </Card>
      )}
      </div>
    </div>
  );
}
